import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Breadcrumb,
  Button,
  InputNumber,
  Modal,
  Select,
  Tag,
  Table,
  Tooltip,
} from "antd";
import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import { useParams, Link } from "react-router-dom";

import { getRequest, postRequest } from "../../../axios";
import {
  ShowErrorMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import "../../../styles/Main.scss";

const PhysicalReceiptConfirmation = ({
  embedded,
  documentIdProp,
  siteIdProp,
  onBack,
} = {}) => {
  const { transferId } = useParams();
  const documentId = documentIdProp ?? transferId;
  const siteId = siteIdProp ?? null;
  const [items, setItems] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);

  useEffect(() => {
    if (!documentId) {
      setLoading(false);
      return;
    }
    const fetchDocumentDistributions = async () => {
      try {
        setLoading(true);
        const params = siteId != null ? { site_id: siteId } : {};
        const res = await getRequest(
          `inventory-goods-receipts/distributions/${documentId}/`,
          params,
        );
        const payload = res?.data;
        const doc = payload?.data;
        const success = payload?.success !== false && doc;

        if (success) {
          const distributions = doc?.distributions ?? [];
          const hasConfirmedReceipt = distributions.some(
            (d) => d.site_receipt_confirmation != null,
          );
          setIsViewMode(!!hasConfirmedReceipt);
          const site_name = distributions[0]?.site_name ?? null;
          setDocumentInfo({
            document_id: doc.document_id,
            document_name: doc.document_name,
            document_file_url: doc.document_file_url,
            invoice_number: doc.invoice_number,
            challan_number: doc.challan_number,
            is_challan: !!doc.is_challan,
            total_items: doc.total_items,
            site_name,
            user_name: payload?.user_name,
          });
          const mapped = distributions.map((dist) => {
            const src = dist.site_receipt_confirmation;
            const actualQty =
              src?.actual_received_quantity != null
                ? src.actual_received_quantity
                : null;
            const reason = src?.reason ?? null;
            return {
              key: String(dist.id),
              id: dist.id,
              distribution_id: dist.distribution_id,
              goods_receipt_id: dist.goods_receipt_id,
              name: dist.item_name ?? "—",
              code: dist.distribution_id,
              expectedQty: dist.quantity ?? 0,
              total_quantity: dist.total_quantity,
              actualQty,
              reason,
              site_name: dist.site_name,
              status: dist.status,
              distributed_by: dist.distributed_by,
              distribution_date: dist.distribution_date,
            };
          });
          setItems(mapped);
        } else {
          setIsViewMode(false);
          setItems([]);
        }
      } catch (err) {
        ShowErrorMsg(
          err?.response?.data?.error || err?.message || "Failed to load",
        );
        setIsViewMode(false);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDocumentDistributions();
  }, [documentId, siteId]);

  const reasonOptions = [
    { label: "Transit Damage", value: "Transit Damage" },
    { label: "Short Supplied", value: "Short Supplied" },
    { label: "Wrong Item", value: "Wrong Item" },
    { label: "Packaging Damage", value: "Packaging Damage" },
    { label: "Other", value: "Other" },
  ];

  const updateActual = (key, value) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const actual = value === null ? null : Math.max(0, value);
        const rejectQty = Math.max(0, row.expectedQty - (actual || 0));
        const reason = rejectQty > 0 ? row.reason : null;
        return { ...row, actualQty: actual, reason };
      }),
    );
  };

  const updateReason = (key, value) => {
    setItems((prev) =>
      prev.map((row) => (row.key === key ? { ...row, reason: value } : row)),
    );
  };

  const rowsWithDerived = useMemo(() => {
    return items.map((row) => {
      const rejectQty = Math.max(0, row.expectedQty - (row.actualQty ?? 0));
      const status =
        row.actualQty === null
          ? null
          : row.actualQty === row.expectedQty
            ? "matched"
            : row.actualQty < row.expectedQty
              ? "mismatch"
              : "excess";
      return { ...row, rejectQty, status };
    });
  }, [items]);

  const disableFinalize = useMemo(() => {
    if (rowsWithDerived.length === 0) return true;
    return rowsWithDerived.some(
      (row) => row.actualQty === null || (row.rejectQty > 0 && !row.reason),
    );
  }, [rowsWithDerived]);

  const handleFinalize = () => {
    setConfirming(true);
    Modal.confirm({
      title: "Finalize Receipt",
      content:
        "Are you sure you want to finalize receipt and update inventory?",
      okText: "Yes, Finalize",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const payload = {
            items: rowsWithDerived.map(
              ({ distribution_id, actualQty, rejectQty, reason }) => ({
                distribution_id,
                actual_received_quantity: actualQty ?? 0,
                transit_reject_quantity: rejectQty ?? 0,
                reason: rejectQty > 0 ? reason : null,
              }),
            ),
          };
          await postRequest(
            "inventory-goods-receipts/finalize-receipt/",
            payload,
          );
          SuccessNotificationMsg(
            "Receipt finalized and inventory updated successfully",
          );
          if (onBack) onBack();
        } catch (err) {
          ShowErrorMsg(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "Failed to finalize receipt",
          );
        } finally {
          setConfirming(false);
        }
      },
      onCancel: () => setConfirming(false),
      afterClose: () => setConfirming(false),
    });
  };

  const columns = [
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.name}</div>
          <div className="muted-text">({record.code})</div>
        </div>
      ),
    },
    {
      title: "Expected Qty (In-Transit)",
      dataIndex: "expectedQty",
      key: "expectedQty",
      width: 140,
      align: "center",
    },
    {
      title: "Actual Received Qty (Counted)",
      dataIndex: "actualQty",
      key: "actualQty",
      width: 180,
      align: "center",
      render: (_, record) =>
        isViewMode ? (
          <span>{record.actualQty != null ? record.actualQty : "—"}</span>
        ) : (
          <InputNumber
            min={0}
            value={record.actualQty}
            onChange={(value) => updateActual(record.key, value)}
          />
        ),
    },
    {
      title: "Transit Reject Qty",
      dataIndex: "rejectQty",
      key: "rejectQty",
      width: 150,
      align: "center",
      render: (_, record) => (
        <span>{record.rejectQty}</span>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 200,
      render: (_, record) =>
        isViewMode ? (
          <span>{record.reason ?? "—"}</span>
        ) : (
          <Select
            allowClear
            disabled={record.rejectQty <= 0}
            placeholder="Select reason"
            value={record.reason}
            onChange={(val) => updateReason(record.key, val)}
            options={reasonOptions}
          />
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 280,
      render: (_, record) => {
        if (record.status === "matched") {
          return (
            <span className="gridstatus">
              <CheckCircleFilled style={{ color: "#52c41a", marginRight: 6 }} />
              Matched
            </span>
          );
        }
        if (record.status === "mismatch") {
          return (
            <span className="gridstatus warning">
              <ExclamationCircleFilled
                style={{ color: "#faad14", marginRight: 6 }}
              />
              Mismatch – Transit Issue
            </span>
          );
        }
        if (record.status === "excess") {
          return (
            <span className="gridstatus warning">
              <ExclamationCircleFilled
                style={{ color: "#faad14", marginRight: 6 }}
              />
              Excess – More than expected
            </span>
          );
        }
        return <span className="muted-text">Awaiting entry</span>;
      },
    },
  ];

  return (
    <>
      {!embedded && (
        <div className="ListingTopBar">
          <div className="pagenamewrap">
            <div className="pagename">
              <h3>
                {isViewMode
                  ? "View Site Inventory Confirmation"
                  : `${documentInfo?.site_name ?? "Site"} – Physical Receipt Confirmation`}
              </h3>
              <Breadcrumb className="Breadcrumb-Sec">
                <Breadcrumb.Item>
                  <Link to="/dashboard">Home</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <Link to="/inventory-site-reciept">
                    Inbound Material Shipments
                  </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Physical Receipt Confirmation</Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </div>
        </div>
      )}

      <section className="grid-sec  tablegridsec">
        <div className="PR-body">
          <div className="po-good-header">
            <div className="po-header-details" style={{ margin: "0" }}>
              <div className="po-row">
                <strong>User:</strong>{" "}
                <span>{documentInfo?.user_name ?? "—"}</span>
              </div>
              <div className="po-row">
                <strong>
                  {documentInfo?.is_challan ? "Challan:" : "Invoice:"}{" "}
                </strong>
                <span>
                  {documentInfo?.is_challan
                    ? (documentInfo?.challan_number ?? "—")
                    : (documentInfo?.invoice_number ?? "—")}
                </span>
              </div>
              <div className="po-row">
                <strong>Site:</strong>{" "}
                <span>{documentInfo?.site_name ?? "—"}</span>
              </div>
            </div>
          </div>
          <div className="tablewrp">
            <Table
              loading={loading}
              columns={columns}
              dataSource={rowsWithDerived}
              pagination={false}
              scroll={{ x: 1000 }}
              rowKey="key"
              locale={{
                emptyText: loading
                  ? null
                  : "No distribution items for this document.",
              }}
            />
          </div>
          {!isViewMode && (
            <div className="FormFooter">
              <div className="good-tge">
                <Tag
                  color="blue"
                  style={{
                    width: "100%",
                    padding: "5px 10px",
                    borderRadius: 4,
                  }}
                >
                  Blind Receiving: User must enter physical count. Not
                  pre-filled.
                </Tag>
              </div>
              <Button
                type="primary"
                className="SaveGradiantBtn"
                disabled={disableFinalize}
                loading={confirming}
                onClick={handleFinalize}
              >
                Finalize Receipt & Update Inventory
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

PhysicalReceiptConfirmation.propTypes = {
  embedded: PropTypes.bool,
  documentIdProp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteIdProp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBack: PropTypes.func,
};

export default PhysicalReceiptConfirmation;
